import {asQueryString, DateUtils} from '@wallaby-ui/core';
import {
  AlertBoardChartBreakdown,
  AlertBoardChartType,
  AlertBoardChartValues,
  AlertBoardSortProperty,
  AlertCategory,
  ALERT_BOARD_CHART_BREAKDOWN_ENUM,
  ALERT_BOARD_CHART_TYPE_ENUM,
  ALERT_BOARD_CHART_VALUES_ENUM,
  ALERT_BOARD_SORT_PROPERTIES,
  ALERT_CATEGORY_ENUM,
  ALERT_ORIGIN_ENUM,
  DEFAULT_ALERT_BOARD_CHART_BREAKDOWN,
  DEFAULT_ALERT_BOARD_CHART_TYPE,
  DEFAULT_ALERT_BOARD_CHART_VALUES,
  Formatters,
  getShortcuts,
  SelectedFilter,
  ShortcutEnum,
  TableSorting,
} from '@wallaby-ui/document';
import {format, parseISO} from 'date-fns';

import {AlertListPageFilters, DEFAULT_ALERT_LIST_FILTERS} from './alertListFilters';
import {HOSPITALIZATION_LIST_COMPONENT, PHARMACOTHERAPY_RECOMMENDATION_LIST_COMPONENT} from './MerieAlertCriteriaChips';

/** The saved-filter component of the board's own saved filters (MERIE `Components.ALERT_BOARD`). */
export const ALERT_BOARD_COMPONENT = 'alertBoard';

/** The lists alerts are defined on in MERIE, in the navigation's order. The Origin options and labels are MERIE's
 * `AlertOriginEnum`, whose values are these component strings. */
export const ALERT_BOARD_ORIGIN_COMPONENTS = [HOSPITALIZATION_LIST_COMPONENT, PHARMACOTHERAPY_RECOMMENDATION_LIST_COMPONENT];

/**
 * The board's filter form, and the stored shape of its saved filters: MERIE's `AlertBoardFilterDto` field for field.
 * The floating shortcut is the FE's alone: MERIE drops it, so the dates are resolved here before every request. The
 * three chart options are saved with the filter, each unset meaning its first value; the chart type is the FE's alone.
 */
export type AlertBoardPageFilters = {
  firstActiveFrom?: string;
  firstActiveTo?: string;
  firstActiveFloatingShortcutName?: ShortcutEnum | null;
  userFilterIds?: number[];
  categories?: AlertCategory[];
  weightFrom?: number;
  weightTo?: number;
  components?: string[];
  includeRetired?: boolean;
  includeDeleted?: boolean;
  chartBreakdown?: AlertBoardChartBreakdown;
  chartValues?: AlertBoardChartValues;
  chartType?: AlertBoardChartType;
};

export const DEFAULT_ALERT_BOARD_FILTERS: AlertBoardPageFilters = {};
export const DEFAULT_ALERT_BOARD_SORTING: TableSorting = {};

const ISO_DATE = 'yyyy-MM-dd';

/**
 * The dates a floating shortcut stands for today ("Last 31 days" saved a month ago means the last 31 days now), or
 * null when the filter holds no shortcut or one the date picker does not know. The month picker's shortcuts come first
 * for the month picker (its "In the past" ends a month back, the day picker's a day back).
 */
export function resolveFloatingFirstActive(
  filters: Pick<AlertBoardPageFilters, 'firstActiveFloatingShortcutName'>,
  dailyView: boolean = true
): {from: string; to: string} | null {
  const shortcut = filters.firstActiveFloatingShortcutName;
  if (!shortcut) return null;
  const all = getShortcuts(false, dailyView);
  const ordered = dailyView
    ? [...all.dailyShortcuts, ...all.monthlyShortcuts, ...all.commonShortcuts]
    : [...all.monthlyShortcuts, ...all.commonShortcuts, ...all.dailyShortcuts];
  const matched = ordered.find((v) => v.label === shortcut);
  if (!matched) return null;
  const {start, end} = matched.getValue();
  return {from: format(start, ISO_DATE), to: format(end, ISO_DATE)};
}

/**
 * The board's range in whole months, as its month picker sets it: a floating shortcut resolved for today, then
 * widened to the months it falls in, as are a day shortcut ("Last 7 days") or fixed days of a filter saved while the
 * board picked days. Null when the filter holds no range.
 */
export function resolveBoardFirstActiveMonths(
  filters: Pick<AlertBoardPageFilters, 'firstActiveFrom' | 'firstActiveTo' | 'firstActiveFloatingShortcutName'>
): {from: string; to: string} | null {
  const floating = resolveFloatingFirstActive(filters, false);
  const from = floating?.from ?? filters.firstActiveFrom;
  const to = floating?.to ?? filters.firstActiveTo;
  if (!from || !to) return null;
  return {
    from: DateUtils.toIsoDateStringLocal(DateUtils.startOfMonth(parseISO(from))),
    to: DateUtils.toIsoDateStringLocal(DateUtils.endOfMonth(parseISO(to))),
  };
}

/**
 * The alerts list's query string behind the board's "Show all", as the reports' "Show all" links replace their list's
 * whole filter: the list's default filter with the board's filters the list has as well, so the list shows what the
 * board counts. The board's months become the days of the first activation, the current month ending today as the
 * list's day picker allows no later day; the alerts, categories, weight and origins are copied. The list's other
 * fields are reset to the default, its paging and sort stay; the board's retired and deleted switches and its chart
 * options have no counterpart there.
 */
export function buildAlertBoardShowAllQuery(filters: AlertBoardPageFilters): string {
  const months = resolveBoardFirstActiveMonths(filters);
  const today = DateUtils.toIsoDateStringLocal(new Date());
  const alerts: AlertListPageFilters = {
    ...DEFAULT_ALERT_LIST_FILTERS,
    firstActiveFrom: months?.from,
    firstActiveTo: months ? (months.to > today ? today : months.to) : undefined,
    userFilterIds: filters.userFilterIds,
    categories: filters.categories,
    weightFrom: filters.weightFrom,
    weightTo: filters.weightTo,
    components: filters.components,
  };
  return asQueryString({alerts});
}

/** The filter as the saved-filter box compares it: a floating range is the shortcut, whatever dates it resolved to. */
export function normalizeAlertBoardFiltersForCompare(filter: AlertBoardPageFilters): AlertBoardPageFilters {
  const copy: AlertBoardPageFilters = {...(filter ?? {})};
  if (copy.firstActiveFloatingShortcutName) {
    delete copy.firstActiveFrom;
    delete copy.firstActiveTo;
  }
  if (!copy.includeRetired) delete copy.includeRetired;
  if (!copy.includeDeleted) delete copy.includeDeleted;
  // a chart option at its first value is the option unset
  if (copy.chartBreakdown === DEFAULT_ALERT_BOARD_CHART_BREAKDOWN) delete copy.chartBreakdown;
  if (copy.chartValues === DEFAULT_ALERT_BOARD_CHART_VALUES) delete copy.chartValues;
  if (copy.chartType === DEFAULT_ALERT_BOARD_CHART_TYPE) delete copy.chartType;
  return copy;
}

/** The filter's own query parameters, shared by the board and its chart: each set as one comma-separated value. */
function buildAlertBoardFilterParams(filters: AlertBoardPageFilters): URLSearchParams {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      if (value.length) params.set(key, value.join(','));
      return;
    }
    params.set(key, String(value));
  };
  set('firstActiveFrom', filters.firstActiveFrom);
  set('firstActiveTo', filters.firstActiveTo);
  set('userFilterIds', filters.userFilterIds);
  set('categories', filters.categories);
  set('weightFrom', filters.weightFrom);
  set('weightTo', filters.weightTo);
  set('components', filters.components);
  params.set('includeRetired', String(Boolean(filters.includeRetired)));
  params.set('includeDeleted', String(Boolean(filters.includeDeleted)));
  return params;
}

/**
 * The query string of `GET /api/alerts/board` and of its `/xlsx` (PDW-253): the filter with both switches always sent
 * and the user's sort, never a page (PDW-413: the table scrolls every alert and the file holds every one). Core appends
 * the default keys the sort leaves out, so the sort sent is only the one the user chose. The chart options are left
 * out, so switching them never rereads the table.
 */
export function buildAlertBoardParams(filters: AlertBoardPageFilters, sorting: TableSorting): URLSearchParams {
  const params = buildAlertBoardFilterParams(filters);
  const field = sorting.field as AlertBoardSortProperty | undefined;
  if (field && sorting.direction && ALERT_BOARD_SORT_PROPERTIES.includes(field)) params.append('sort', `${field},${sorting.direction}`);
  return params;
}

/**
 * The query string of `GET /api/alerts/board/chart`: the board's filter, the breakdown and the values, always sent, and
 * no page and no sort. The chart type is left out: only the FE draws by it, so switching it never rereads the chart.
 */
export function buildAlertBoardChartParams(filters: AlertBoardPageFilters): URLSearchParams {
  const params = buildAlertBoardFilterParams(filters);
  params.set('chartBreakdown', filters.chartBreakdown ?? DEFAULT_ALERT_BOARD_CHART_BREAKDOWN);
  params.set('chartValues', filters.chartValues ?? DEFAULT_ALERT_BOARD_CHART_VALUES);
  return params;
}

type AlertBoardChipLookups = {
  getAlertLabel: (id: number) => string;
};

/** The "Active filters" chips of the board, one per filter, in the order of the filter card. A switch shows only when on. */
export function buildAlertBoardSelectedFilters(
  filters: AlertBoardPageFilters,
  formatters: Pick<Formatters, 't' | 'tEnum' | 'formatDate'>,
  lookups: AlertBoardChipLookups
): SelectedFilter[] {
  const {t, tEnum, formatDate} = formatters;
  const normalized = normalizeAlertBoardFiltersForCompare(filters);
  return [
    {
      name: ['firstActiveFrom', 'firstActiveTo', 'firstActiveFloatingShortcutName'],
      label: t('alert.board.filter.dateRange'),
      value: `${formatDate(filters.firstActiveFrom ?? '')} - ${formatDate(filters.firstActiveTo ?? '')}`,
    },
    {
      name: 'userFilterIds',
      label: t('alert.board.filter.alert'),
      value: (filters.userFilterIds ?? []).map(lookups.getAlertLabel).join(', '),
    },
    {
      name: 'categories',
      label: t('alert.board.filter.category'),
      value: (filters.categories ?? []).map((v) => tEnum(ALERT_CATEGORY_ENUM, v)).join(', '),
    },
    {
      name: ['weightFrom', 'weightTo'],
      label: t('alert.weight'),
      value: `${filters.weightFrom ?? ''} - ${filters.weightTo ?? ''}`,
    },
    {
      name: 'components',
      label: t('hai.properties.origin'),
      value: (filters.components ?? []).map((v) => tEnum(ALERT_ORIGIN_ENUM, v)).join(', '),
    },
    {
      name: 'includeRetired',
      label: t('alert.board.filter.includeRetired'),
      value: filters.includeRetired ? t('global.boolFilter.true') : undefined,
    },
    {
      name: 'includeDeleted',
      label: t('alert.board.filter.includeDeleted'),
      value: filters.includeDeleted ? t('global.boolFilter.true') : undefined,
    },
    {
      name: 'chartBreakdown',
      label: t('alert.board.filter.chartBreakdown'),
      value: normalized.chartBreakdown ? tEnum(ALERT_BOARD_CHART_BREAKDOWN_ENUM, normalized.chartBreakdown) : undefined,
    },
    {
      name: 'chartValues',
      label: t('department.report.filter.chartvalues'),
      value: normalized.chartValues ? tEnum(ALERT_BOARD_CHART_VALUES_ENUM, normalized.chartValues) : undefined,
    },
    {
      name: 'chartType',
      label: t('department.report.charts.type'),
      value: normalized.chartType ? tEnum(ALERT_BOARD_CHART_TYPE_ENUM, normalized.chartType) : undefined,
    },
  ];
}

/** The filter object the chips are checked against: a switch that is off, or a chart option at its first value, holds no chip. */
export function alertBoardTableFiltersForChips(filters: AlertBoardPageFilters): Record<string, any> {
  const normalized = normalizeAlertBoardFiltersForCompare(filters);
  return {
    ...filters,
    includeRetired: filters.includeRetired ? true : undefined,
    includeDeleted: filters.includeDeleted ? true : undefined,
    chartBreakdown: normalized.chartBreakdown,
    chartValues: normalized.chartValues,
    chartType: normalized.chartType,
  };
}
